import { useLayoutEffect, useState } from 'react';
import { Overlay } from '../ui/Overlay';
import { Tooltip } from '../ui/Tooltip';
import { helpTip } from '../lib/help';

/* ---------------------------------------------------------------------------
   Types
   --------------------------------------------------------------------------- */

export type ComposeTile =
  | 'message'
  | 'ask'
  | 'question'
  | 'kudos'
  | 'note'
  | 'idea'
  | 'memory'
  | 'date'
  | 'link';

export interface ComposeTileDef {
  type: ComposeTile;
  label: string;
  subtitle: string;
  /** One-line in-context explanation (card Cibmk2YuSlFB). */
  tip: string;
  icon: React.ReactNode;
}

/* ---------------------------------------------------------------------------
   Tile definitions — nine tiles, a 3x3 grid (card RvESgw7lVASX)

   Meeting is gone from here on purpose: Life → Plan owns the weekly meeting
   now (card 7aTCRQ9j8PWV), and the More menu already links it as "Weekly
   meeting". A second capture surface in the + menu was the duplicate, not the
   feature. Memory / Date idea / Link join because they are the three notebook
   captures a partner reaches for without leaving the thread.
   --------------------------------------------------------------------------- */

/* Exported for the Thread rail's quick-entry tiles (card uLr0sCxQDnd7, A1c):
   the rail header reuses these same defs — one icon set, one label set, no
   second tile table to drift from the + menu. */
export const TILE_DEFS: ComposeTileDef[] = [
  {
    type: 'message',
    label: 'Message',
    subtitle: 'Just say it',
    tip: helpTip('compose-message'),
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    ),
  },
  {
    type: 'ask',
    label: 'Ask',
    subtitle: 'A favour or chore',
    tip: helpTip('compose-ask'),
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="m3 11 18-5v12L3 14v-3z"/>
        <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>
      </svg>
    ),
  },
  {
    type: 'question',
    label: 'Question',
    subtitle: 'A daily question',
    tip: helpTip('compose-question'),
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <path d="M9.6 9a2.6 2.6 0 0 1 4.9 1c0 1.8-2.5 2.4-2.5 3.1"/>
        <path d="M12 17h.01"/>
      </svg>
    ),
  },
  {
    type: 'kudos',
    label: 'Kudos',
    subtitle: 'Say that was lovely',
    tip: helpTip('compose-kudos'),
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21.2l7.8-7.8 1-1a5.5 5.5 0 0 0 0-7.8z"/>
      </svg>
    ),
  },
  {
    type: 'note',
    label: 'Note',
    subtitle: 'Save a thought',
    tip: helpTip('compose-note'),
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
      </svg>
    ),
  },
  {
    type: 'idea',
    label: 'Idea',
    subtitle: 'Something to try',
    tip: helpTip('compose-idea'),
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 18h6"/>
        <path d="M10 22h4"/>
        <path d="M12 2a6 6 0 0 0-3.4 10.9c.8.6 1.4 1.6 1.4 2.6h4c0-1 .6-2 1.4-2.6A6 6 0 0 0 12 2z"/>
      </svg>
    ),
  },
  {
    type: 'memory',
    label: 'Memory',
    subtitle: 'A moment to keep',
    tip: helpTip('compose-memory'),
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <path d="m21 15-5-5L5 21"/>
      </svg>
    ),
  },
  {
    type: 'date',
    label: 'Date idea',
    subtitle: 'Something to plan',
    tip: helpTip('compose-date'),
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
        <path d="M8 14h.01"/>
        <path d="M12 14h.01"/>
        <path d="M16 14h.01"/>
        <path d="M8 18h.01"/>
        <path d="M12 18h.01"/>
      </svg>
    ),
  },
  {
    type: 'link',
    label: 'Collection',
    subtitle: 'Worth a look',
    tip: helpTip('compose-link'),
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
      </svg>
    ),
  },
];

/* ---------------------------------------------------------------------------
   Component
   --------------------------------------------------------------------------- */

interface ComposeMenuProps {
  open: boolean;
  onClose: () => void;
  /** When the user picks a tile. Called with the tile key, then closes. */
  onPick?: (tile: ComposeTile) => void;
}

export function ComposeMenu({ open, onClose, onPick }: ComposeMenuProps) {
  /* Panel height is capped to the space actually ABOVE the composer (card
     EQDNwPnepGuw). The menu opens upward off `bottom: calc(100% + 4px)`, which
     resolves against .pear-chat-input-area's own height (~136px) — so the
     panel's 400px of tiles grew from y=350 straight off the TOP of a 600px
     viewport (panel top -49.9, three tiles unreachable). The old
     `maxHeight: min(75vh, 520px)` never engaged: 75vh at 600px is 450px, and
     the panel's natural height (400px) sits below it, so nothing ever capped
     it. The cap has to come from the anchor's distance to the viewport top,
     which is only knowable at runtime — measured here, re-measured on resize
     while open (a keyboard opening, a rotation, or a desktop window drag all
     move the composer). */
  const [panelMaxH, setPanelMaxH] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    if (!open) return undefined;
    const measure = () => {
      const anchor = document.querySelector('.pear-chat-input-area');
      if (!anchor) { setPanelMaxH(undefined); return; }
      const top = anchor.getBoundingClientRect().top;
      // 8px of headroom so the top row never kisses the clip line — the same
      // failure mode the landing sign-in pill had (card 3FKmEd9C9FCt).
      setPanelMaxH(Math.max(160, Math.floor(top - 8)));
    };
    measure();
    window.addEventListener('resize', measure);
    // the composer can move without a window resize (tab switch, history load)
    const id = window.setInterval(measure, 250);
    return () => { window.removeEventListener('resize', measure); window.clearInterval(id); };
  }, [open]);

  /* A11y comes from the shared Overlay primitive (UX item 6): focus lands on
     the first tile, Tab cycles through the nine, Escape closes and hands focus
     back to the + button, and a tap ANYWHERE outside dismisses — the old
     backdrop only covered the floating panel itself, so a tap on the page
     behind left the menu open over it.

     portal={false} on purpose: the menu is anchored to the composer block
     (`position: absolute` against .pear-chat-input-area), so it must stay in
     its own positioning context. That also means it cannot make the page
     behind it inert — the Overlay only promises aria-modal when it really
     does — so the panel is a plain dialog with a focus trap, not a modal one. */
  return (
    <Overlay
      open={open}
      onClose={onClose}
      className="pear-compose-menu-overlay"
      style={{
        position: 'absolute',
        /* Opens UPWARD now (card CwbqiUPwRg02): the composer is docked at
           the BOTTOM of the frame again, so `bottom: calc(100% + 4px)` seats
           the menu just above the input, inside the viewport at 390x844,
           360x800 and 1440x900. The downward `top: 100%` anchor that
           dZjN98QXAgEH set for the top dock would throw it off the bottom
           edge now — the same off-screen failure ZoS0PBKCVqKA fixed, from
           the other side. */
        bottom: 'calc(100% + 4px)',
        left: 0,
        right: 0,
        zIndex: 100,
        display: 'flex',
        justifyContent: 'center',
        padding: '0 8px',
      }}
      panelClassName="pear-compose-menu"
      panelStyle={{
        background: 'var(--color-surface-dark, #14342b)',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        display: 'flex',
        flexDirection: 'column',
        padding: '4px 8px 12px',
        maxWidth: 340,
        width: '100%',
        /* Capped to the space actually above the composer, measured on open
           (card EQDNwPnepGuw) — see panelMaxH above. The old fixed
           `min(75vh, 520px)` never engaged on a short viewport: 75vh at 600px
           is 450px and the panel's natural height (400px) sits under it, so
           nothing stopped the tiles growing off the top edge. Below the
           measurement (first paint, or a surface with no composer) fall back
           to the old cap so the menu is still usable. */
        maxHeight: panelMaxH ?? 'min(75vh, 520px)',
        overflowY: 'auto',
      }}
      label="Compose — pick what you're adding"
      portal={false}
      closeOnOutsidePointer
      /* The ✕ is the first thing in the panel, but focus still lands on the
         first TILE: the menu's job is to offer the nine, and a screen reader
         user who opened it to add a memory should not have to Tab past the
         way out to reach it (card 5PATFTTEaHKJ). */
      initialFocus=".pear-compose-tile"
    >
      {/* One visible way out (card 5PATFTTEaHKJ): until now the menu closed on
          a tap outside or Escape, and neither is visible on a phone — you had
          to know the gesture. Same job as MoreMenu's Close, 44px target. */}
      <div className="pear-compose-head">
        <button
          type="button"
          className="pear-compose-close"
          aria-label="Close the menu"
          data-testid="compose-menu-close"
          onClick={onClose}
        >
          ✕
        </button>
      </div>
      <div className="pear-compose-grid">
      {TILE_DEFS.map((def) => (
        /* The ⓘ trigger used to sit INLINE after the button, so the button
           shrink-wrapped to its label and the icon+text pair landed up to
           25px left of the cell's centre (card svmgRALhXjiq). The cell is
           now the positioning context: the button fills it, and the trigger
           is pinned to the corner — same 44px target, zero layout cost. */
        <div key={def.type} className="pear-compose-cell">
        <button
          className="pear-compose-tile"
          title={def.label}
          onClick={() => onPick?.(def.type)}
        >
          <span className="pear-compose-tile-icon">{def.icon}</span>
          <span className="pear-compose-tile-label">{def.label}</span>
          <span className="pear-compose-tile-sub">{def.subtitle}</span>
        </button>
        <Tooltip label={def.tip} tipFor={`tile-${def.type}`}>
        </Tooltip>
        </div>
      ))}
      </div>
    </Overlay>
  );
}
